/* eslint-disable react/prop-types */
import React, { useState, useEffect } from 'react';
import { withRouter } from 'react-router-dom';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/styles';
import styles from '../../../style';
import moment from 'moment';
import PerfectScrollbar from 'react-perfect-scrollbar';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  IconButton,
  InputAdornment,
  CircularProgress,
  Tooltip,
} from '@material-ui/core';
import DatePicker from 'react-datepicker';
import {
  CheckOutlined,
  ClearOutlined,
  Edit,
  DeleteOutline,
  CalendarToday,
} from '@material-ui/icons';
import { connect } from 'react-redux';
import {
  saveHealthMaintenance,
  updateHealthMaintenance,
  deleteHealthMaintenance,
  getPatientMedicalHistory,
} from 'services/soap/action';

// ---------------------------------------------------------------------------
// TipButton: IconButton wrapped in a Tooltip.
// The <span> wrapper ensures the tooltip renders even when the button is disabled.
// ---------------------------------------------------------------------------
const TipButton = ({ title, children, ...rest }) => (
  <Tooltip title={title} arrow>
    <span style={{ display: 'inline-flex' }}>
      <IconButton {...rest}>{children}</IconButton>
    </span>
  </Tooltip>
);

const HealthMaintenance = props => {
  const { classes, patientId, getPatientMedicalHistory, ptMedicalHistory } = props;

  const initialUserState = {
    id: 0,
    name: '',
    recordedDate: new Date(),
    findings: '',
  };

  const [values, setValues] = useState(initialUserState);
  const [editvalues, setEditValues] = useState(initialUserState);
  const [HM, setHM] = useState(null);
  const [isEdit, setIsEdit] = useState(false);

  // ---------------------------------------------------------------------------
  // Loading states
  // 'save'        — header row save button
  // 'update'      — inline update confirm button
  // `delete-{id}` — delete spinner on that specific row
  // ---------------------------------------------------------------------------
  const [loadingId, setLoadingId] = useState(null);

  // ---------------------------------------------------------------------------
  // Sync Redux → local state
  // Guard removed so an empty array properly clears local state too
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (ptMedicalHistory?.healthMaintenance !== undefined)
      setHM(ptMedicalHistory.healthMaintenance || []);
  }, [ptMedicalHistory]);

  // ---------------------------------------------------------------------------
  // Field handlers
  // ---------------------------------------------------------------------------
  const handleFieldChange = (field, value) =>
    setValues({ ...values, [field]: value });

  const handleEditFieldChange = (field, value) =>
    setEditValues({ ...editvalues, [field]: value });

  // ---------------------------------------------------------------------------
  // Save new health maintenance
  // ---------------------------------------------------------------------------
  const handleSaveHealthMaintenance = async () => {
    if (values.name || values.findings) {
      setLoadingId('save');
      try {
        const res = await props.saveHealthMaintenance(patientId, values);
        if (res.status) {
          getPatientMedicalHistory(patientId);
          setValues(initialUserState);
          setIsEdit(false);
        }
      } finally {
        setLoadingId(null);
      }
    }
  };

  // ---------------------------------------------------------------------------
  // Update existing health maintenance
  // ---------------------------------------------------------------------------
  const handleUpdateHealthMaintenance = async idx => {
    if (editvalues.name || editvalues.findings) {
      setLoadingId('update');
      try {
        const res = await props.updateHealthMaintenance(patientId, editvalues);
        if (res.status) {
          getPatientMedicalHistory(patientId);
          setIsEdit(false);
        }
      } finally {
        setLoadingId(null);
      }
    }
  };

  // ---------------------------------------------------------------------------
  // Delete health maintenance
  // ---------------------------------------------------------------------------
  const handleDeleteHealthMaintenance = async id => {
    if (loadingId !== null) return;
    setLoadingId(`delete-${id}`);
    try {
      const res = await props.deleteHealthMaintenance(patientId, id);
      if (res.status) {
        getPatientMedicalHistory(patientId);
        setValues(initialUserState);
      }
    } finally {
      setLoadingId(null);
    }
  };

  // ---------------------------------------------------------------------------
  // Edit / clear
  // ---------------------------------------------------------------------------
  const handleEditHealthMaintenance = (data, index) => {
    setIsEdit(true);
    if (data)
      setEditValues({
        id: data.id,
        name: data.name,
        recordedDate: data.recordedDate,
        findings: data.findings,
        editIndex: index,
      });
  };

  const clearHealthMaintenance = () => {
    setIsEdit(false);
    setValues(initialUserState);
  };

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  return (
    <div className={classes.templateWrap}>
      <div className={classes.templateBody} style={{ borderTop: 'default' }}>
        <PerfectScrollbar>
        <div style={{ maxHeight: 'calc(100vh - 298px)' }}>
          <Table className={classes.templateTable}>

            {/* ==============================================================
                HEADER ROW — add new health maintenance
            ============================================================== */}
            <TableHead>
              <TableRow>
                <TableCell width="20%">
                  <TextField
                    className={classes.textBox}
                    InputProps={{ classes: { notchedOutline: classes.notchedOutline } }}
                    onChange={e => handleFieldChange('name', e.target.value)}
                    placeholder="Input Name"
                    value={values.name}
                    variant="outlined"
                  />
                </TableCell>
                <TableCell width="30%">
                  <DatePicker
                    customInput={
                      <TextField
                        className={classes.textField}
                        fullWidth
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <CalendarToday style={{ height: 16, width: 16 }} />
                            </InputAdornment>
                          ),
                        }}
                        margin="dense"
                        variant="outlined"
                      />
                    }
                    dateFormat="MM/dd/yyyy"
                    onBlur={e => (e.target.placeholder = 'Date Recorded')}
                    onChange={date => handleFieldChange('recordedDate', date)}
                    onFocus={e => (e.target.placeholder = '')}
                    placeholderText="Date Recorded"
                    selected={values.recordedDate && new Date(values.recordedDate)}
                    showMonthDropdown
                    showYearDropdown
                  />
                </TableCell>
                <TableCell width="60%">
                  <TextField
                    className={classes.textBox}
                    InputProps={{ classes: { notchedOutline: classes.notchedOutline } }}
                    onChange={e => handleFieldChange('findings', e.target.value)}
                    placeholder="Input Findings"
                    value={values.findings}
                    variant="outlined"
                  />
                </TableCell>
                <TableCell align="center" style={{ whiteSpace: 'nowrap' }}>
                  <TipButton
                    title="Save"
                    disabled={loadingId === 'save'}
                    onClick={handleSaveHealthMaintenance}
                    size="small"
                    style={{ color: '#03A484' }}>
                    {loadingId === 'save'
                      ? <CircularProgress size={16} color="inherit" />
                      : <CheckOutlined />}
                  </TipButton>
                  <TipButton
                    title="Clear"
                    disabled={loadingId === 'save'}
                    onClick={clearHealthMaintenance}
                    size="small">
                    <ClearOutlined style={{ color: '#DE5555' }} />
                  </TipButton>
                </TableCell>
              </TableRow>
            </TableHead>

            {/* ==============================================================
                BODY — existing health maintenance rows
            ============================================================== */}
            <TableBody>
              {HM &&
                HM.length > 0 &&
                HM.map((item, idx) => (
                  <TableRow key={item.id}>
                    {isEdit && idx === editvalues.editIndex ? (
                      <>
                        <TableCell width="20%">
                          <TextField
                            className={classes.textBox}
                            InputProps={{ classes: { notchedOutline: classes.notchedOutline } }}
                            onChange={e => handleEditFieldChange('name', e.target.value)}
                            placeholder="Input Name"
                            value={editvalues.name}
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell width="30%">
                          <DatePicker
                            customInput={
                              <TextField
                                className={classes.textField}
                                fullWidth
                                InputProps={{
                                  endAdornment: (
                                    <InputAdornment position="end">
                                      <CalendarToday style={{ height: 16, width: 16 }} />
                                    </InputAdornment>
                                  ),
                                }}
                                margin="dense"
                                variant="outlined"
                              />
                            }
                            dateFormat="MM/dd/yyyy"
                            onBlur={e => (e.target.placeholder = 'Date Recorded')}
                            onChange={date => handleEditFieldChange('recordedDate', date)}
                            onFocus={e => (e.target.placeholder = '')}
                            placeholderText="Date Recorded"
                            selected={editvalues.recordedDate && new Date(editvalues.recordedDate)}
                            showMonthDropdown
                            showYearDropdown
                          />
                        </TableCell>
                        <TableCell width="60%">
                          <TextField
                            className={classes.textBox}
                            InputProps={{ classes: { notchedOutline: classes.notchedOutline } }}
                            onChange={e => handleEditFieldChange('findings', e.target.value)}
                            placeholder="Input Findings"
                            value={editvalues.findings}
                            variant="outlined"
                          />
                        </TableCell>
                      </>
                    ) : (
                      <>
                        <TableCell>{item?.name}</TableCell>
                        <TableCell>
                          {item?.recordedDate
                            ? moment(item.recordedDate).format('MM/DD/YYYY')
                            : ''}
                        </TableCell>
                        <TableCell>{item?.findings}</TableCell>
                      </>
                    )}
                    <TableCell align="center" width={72}>
                      {isEdit && idx === editvalues.editIndex ? (
                        <>
                          <TipButton
                            title="Edit"
                            disabled={loadingId === 'update'}
                            onClick={() => handleUpdateHealthMaintenance(idx)}
                            size="small"
                            style={{ color: '#03A484' }}>
                            {loadingId === 'update'
                              ? <CircularProgress size={16} color="inherit" />
                              : <CheckOutlined />}
                          </TipButton>
                          <TipButton
                            title="Cancel"
                            disabled={loadingId === 'update'}
                            onClick={clearHealthMaintenance}
                            size="small">
                            <ClearOutlined style={{ color: '#DE5555' }} />
                          </TipButton>
                        </>
                      ) : (
                        <>
                          <TipButton
                            title="Edit"
                            disabled={loadingId !== null}
                            onClick={() => handleEditHealthMaintenance(item, idx)}
                            size="small">
                            <Edit color="primary" />
                          </TipButton>
                          <TipButton
                            title="Delete"
                            disabled={loadingId !== null}
                            onClick={() => handleDeleteHealthMaintenance(item.id)}
                            size="small">
                            {loadingId === `delete-${item.id}`
                              ? <CircularProgress size={16} color="inherit" />
                              : <DeleteOutline style={{ color: '#DE5555' }} />}
                          </TipButton>
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </div>
        </PerfectScrollbar>
      </div>
    </div>
  );
};

HealthMaintenance.propTypes = {
  className: PropTypes.string,
};

const mapDispatchToProps = {
  saveHealthMaintenance,
  updateHealthMaintenance,
  deleteHealthMaintenance,
  getPatientMedicalHistory,
};

const mapStateToProps = state => ({
  ptMedicalHistory: state.soap && state.soap.ptMedicalHistory,
});

export default withRouter(
  connect(mapStateToProps, mapDispatchToProps)(withStyles(styles)(HealthMaintenance))
);